import type { SinglePayment } from '../../../../services/SinglePayments';

export type SinglePaymentStatusKind =
  | 'approved'
  | 'rejected'
  | 'processing'
  | 'unknown';

const APPROVED_STATUS_CODES = new Set([
  '2',
  'approved',
  'aprobado',
  'paid',
  'pagado',
  'completed',
  'completado'
]);

const REJECTED_STATUS_CODES = new Set([
  '3',
  '4',
  'rejected',
  'rechazado',
  'failed',
  'fallido',
  'cancelled',
  'canceled',
  'cancelado'
]);

const PROCESSING_STATUS_CODES = new Set([
  '0',
  '1',
  'pending',
  'pendiente',
  'processing',
  'procesando',
  'in_process',
  'in progress',
  'en_proceso',
  'en proceso'
]);

function normalizeStatusValue(status: SinglePayment['status'] | null | undefined): string {
  return String(status ?? '').trim().toLowerCase();
}

function includesAny(value: string, expectedFragments: string[]): boolean {
  return expectedFragments.some((fragment) => value.includes(fragment));
}

export function getSinglePaymentStatusKind(
  status: SinglePayment['status'] | null | undefined
): SinglePaymentStatusKind {
  const normalizedStatus = normalizeStatusValue(status);

  if (!normalizedStatus) {
    return 'unknown';
  }

  if (
    APPROVED_STATUS_CODES.has(normalizedStatus) ||
    includesAny(normalizedStatus, ['aprob', 'pagad', 'complet', 'paid'])
  ) {
    return 'approved';
  }

  if (
    REJECTED_STATUS_CODES.has(normalizedStatus) ||
    includesAny(normalizedStatus, ['rechaz', 'fall', 'cancel'])
  ) {
    return 'rejected';
  }

  if (
    PROCESSING_STATUS_CODES.has(normalizedStatus) ||
    includesAny(normalizedStatus, ['pend', 'proces', 'review'])
  ) {
    return 'processing';
  }

  return 'unknown';
}

export function getSinglePaymentStatusLabel(
  status: SinglePayment['status'] | null | undefined
): string {
  switch (getSinglePaymentStatusKind(status)) {
    case 'approved':
      return 'Aprobado';
    case 'rejected':
      return 'Rechazado';
    case 'processing':
      return 'En revisión';
    default:
      return 'No confirmado';
  }
}

export function isApprovedSinglePayment(
  status: SinglePayment['status'] | null | undefined
): boolean {
  return getSinglePaymentStatusKind(status) === 'approved';
}
